import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MdTrendingUp, MdGroup, MdBolt, MdEmojiEvents, MdFitnessCenter, MdCalendarMonth, MdFlag, MdRestaurant } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import { useAuthUser } from '../../hooks/useAuthUser';
import { encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import { trackEvent } from '../../utils/analytics';
import style from './OnboardingFlow.module.css';

const API_URL = process.env.REACT_APP_API_URL;

const SELECTION_DISPLAY = [
	{ key: 'goal', label: 'Goal', icon: MdFlag },
	{ key: 'experienceLevel', label: 'Level', icon: MdTrendingUp },
	{ key: 'trainingDaysPerWeek', label: 'Days / week', icon: MdCalendarMonth, suffix: ' days' },
	{ key: 'equipment', label: 'Equipment', icon: MdFitnessCenter },
	{ key: 'dietPreference', label: 'Diet', icon: MdRestaurant },
];

const OnboardingPreview = () => {
	const { routineId } = useParams();
	const { user, isLoaded } = useAuthUser();
	const navigate = useNavigate();
	const [routine, setRoutine] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState('');

	useEffect(() => {
		if (routineId) {
			trackEvent('onboarding_view_preview', { routineId });
		}
	}, [routineId]);

	useEffect(() => {
		if (!isLoaded) return;
		if (!user?.id) {
			navigate(`/login?redirectTo=${encodeURIComponent(`/onboarding/preview/${routineId}`)}`, { replace: true });
			return;
		}

		const loadDraftRoutine = async () => {
			setIsLoading(true);
			setLoadError('');
			try {
				const response = await requestWithApiFallback(API_URL, (base) => axios.get(
					`${base}/onboarding/routine/${encodePathSegment(user.id)}/${encodePathSegment(routineId)}`,
					{ withCredentials: true }
				));
				setRoutine(response.data?.routine || null);
			} catch (error) {
				console.error('Failed to load onboarding routine preview:', error);
				setLoadError('Could not load your preview right now.');
			} finally {
				setIsLoading(false);
			}
		};

		loadDraftRoutine();
	}, [isLoaded, navigate, routineId, user?.id]);

	const inputs = useMemo(() => routine?.onboardingInputs || {}, [routine]);

	const handleUnlock = () => {
		trackEvent('onboarding_click_unlock', { routineId });
		navigate(`/upgrade?source=onboarding&routineId=${encodeURIComponent(routineId)}`);
	};

	if (isLoading) {
		return (
			<div className={style.LoadingWrap}>
				<p>Preparing your plan...</p>
			</div>
		);
	}

	if (loadError || !routine) {
		return (
			<div className={style.OnboardingPage}>
				<div className={style.OnboardingCard}>
					<p className={style.MutedText}>{loadError || 'No preview available yet.'}</p>
					<SecondaryButton text="Back to personalization" click={() => navigate('/onboarding/personalize')} width="100%" />
				</div>
			</div>
		);
	}

	return (
		<div className={style.OnboardingPage}>
			<SEO
				title="Your personalized plan is ready"
				description="Your 30-minute workout plan has been tailored to your goals. Unlock it to get started."
				path={`/onboarding/preview/${routineId}`}
			/>

			<div className={style.OnboardingCard}>
				<span className={style.StepLabel}>Step 3 of 3</span>

				<section className={style.PlanTitleSection}>
					<h1 className={style.PlanTitle}>{routine.routineName}</h1>
					<p className={style.PlanSubtitle}>
						Your plan is tailored and ready to go. Unlock it to get your full weekly workouts and meal plan.
					</p>
				</section>

				<section className={style.SelectionsSummary}>
					<span className={style.SummaryLabel}>Your selections</span>
					<div className={style.SelectionsGrid}>
						{SELECTION_DISPLAY.map(({ key, label, icon: Icon, suffix }) => {
							const value = inputs[key];
							if (!value) return null;
							return (
								<div key={key} className={style.SelectionSummaryItem}>
									<Icon className={style.SelectionSummaryIcon} />
									<div className={style.SelectionSummaryText}>
										<span className={style.SelectionSummaryLabel}>{label}</span>
										<span className={style.SelectionSummaryValue}>{value}{suffix || ''}</span>
									</div>
								</div>
							);
						})}
					</div>
				</section>

				<section className={style.FomoSection}>
					<div className={style.FomoHighlight}>
						<MdGroup className={style.FomoIcon} />
						<span className={style.FomoStat}>2,400+ people</span>
						<span className={style.FomoDesc}>have already generated their routines and meal plans this month</span>
					</div>

					<div className={style.FomoGrid}>
						<div className={style.FomoCard}>
							<MdBolt className={style.FomoCardIcon} />
							<span className={style.FomoCardTitle}>AI-Generated Plans</span>
							<span className={style.FomoCardText}>They&apos;re not guessing — Cali builds their routines and meals in seconds. You&apos;re one tap away.</span>
						</div>
						<div className={style.FomoCard}>
							<MdTrendingUp className={style.FomoCardIcon} />
							<span className={style.FomoCardTitle}>Climbing the Ranks</span>
							<span className={style.FomoCardText}>Every workout logged earns XP. They&apos;re stacking streaks and moving up the Calcium leaderboard right now.</span>
						</div>
						<div className={style.FomoCard}>
							<MdEmojiEvents className={style.FomoCardIcon} />
							<span className={style.FomoCardTitle}>Don&apos;t Fall Behind</span>
							<span className={style.FomoCardText}>The longer you wait, the more ground you lose. Your personalized plan is ready — just start.</span>
						</div>
					</div>

					<p className={style.FomoUrgency}>Your plan was built from your goals. Unlock it before you lose momentum.</p>
				</section>

				<div className={style.CTAStack}>
					<PrimaryButton text="Unlock My Plan" click={handleUnlock} width="100%" />
					<p className={style.ProofLine}>Join the people already getting results on Calcium.</p>
					<div className={style.Microcopy}>
						<span>Secure checkout</span>
						<span className={style.DividerDot} />
						<span>Cancel anytime</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OnboardingPreview;
